import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '@/i18n';
import api from './api';
import { tokenStorage } from './tokenStorage';

const AuthCtx = createContext(null);

function syncI18n(u) {
  const lang = u?.language;
  if (lang && i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('ev_user') || 'null');
      if (u) syncI18n(u);
      return u;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(!!tokenStorage.get());

  useEffect(() => {
    const token = tokenStorage.get();
    if (token && !user) {
      api.get('/auth/me')
        .then((r) => {
          setUser(r.data);
          localStorage.setItem('ev_user', JSON.stringify(r.data));
          syncI18n(r.data);
        })
        .catch(() => { tokenStorage.clear(); localStorage.removeItem('ev_user'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    tokenStorage.set(data.token);
    localStorage.setItem('ev_user', JSON.stringify(data.user));
    syncI18n(data.user);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    tokenStorage.set(data.token);
    localStorage.setItem('ev_user', JSON.stringify(data.user));
    syncI18n(data.user);
    setUser(data.user);
    return data.user;
  };

  const refreshUser = (u) => {
    if (!u) return;
    localStorage.setItem('ev_user', JSON.stringify(u));
    syncI18n(u);
    setUser(u);
  };

  const logout = () => {
    // Fire-and-forget: clear the server-side httpOnly cookie. We don't await
    // because the user is leaving anyway and we don't want to block the redirect.
    api.post('/auth/logout').catch(() => {});
    tokenStorage.clear();
    localStorage.removeItem('ev_user');
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthCtx.Provider value={useMemo(
      () => ({ user, loading, login, register, logout, refreshUser }),
      [user, loading]
    )}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
